import React from 'react';

const Sustainability = () => {
  return (
    <main className="w-full">
      <section className="w-full bg-orange-50 py-16">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="flex flex-col justify-center gap-6 text-center lg:text-left">
            <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-coffee-dark leading-tight tracking-tight">
              Sustainability at Funzo Coffee
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              From ethical sourcing to eco-friendly packaging, we turn lessons—Mafunzo—into action. We’re redefining Uganda’s place in the global coffee value chain by investing in people, protecting nature, and delivering quality you can feel good about.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <a href="#impact" className="px-6 py-3 bg-coffee-primary text-white rounded-lg shadow-md transition-all duration-150 hover:bg-coffee-dark hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-coffee-accent">
                See Our Impact
              </a>
              <a href="/coffee" className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-coffee-primary transition-colors duration-200 hover:text-coffee-secondary hover:border-coffee-secondary focus:outline-none focus:ring-2 focus:ring-coffee-accent">
                Support Sustainable Coffee
              </a>
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden shadow-xl">
            <img src="https://app.grapesjs.com/api/assets/random-image?query=%22uganda%20coffee%20farm%22&w=1200&h=800" alt="Ugandan coffee farm at sunrise" className="w-full h-96 object-cover"/>
          </div>
        </div>
      </section>
      <section id="impact" className="w-full bg-white py-16 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8">
            <h2 className="font-serif text-4xl text-coffee-dark">Our Impact in Numbers</h2>
            <p className="mt-2 text-sm text-gray-600">Measurable progress towards ethical, environmental, and community goals.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 bg-orange-50 rounded-xl border border-gray-200 flex items-start gap-4">
              <img src="https://api.iconify.design/lucide-leaf.svg?color=%235C3A21" alt="Leaf icon" className="w-6 h-6"/>
              <div>
                <div className="text-3xl font-bold text-coffee-secondary">1,800+</div>
                <div className="mt-2 text-sm text-gray-600">Smallholder farmers supported</div>
              </div>
            </div>
            <div className="p-6 bg-orange-50 rounded-xl border border-gray-200 flex items-start gap-4">
              <img src="https://api.iconify.design/lucide-droplets.svg?color=%235C3A21" alt="Water icon" className="w-6 h-6"/>
              <div>
                <div className="text-3xl font-bold text-coffee-secondary">42%</div>
                <div className="mt-2 text-sm text-gray-600">Avg. water usage reduction on partner farms</div>
              </div>
            </div>
            <div className="p-6 bg-orange-50 rounded-xl border border-gray-200 flex items-start gap-4">
              <img src="https://api.iconify.design/lucide-recycle.svg?color=%235C3A21" alt="Recycle icon" className="w-6 h-6"/>
              <div>
                <div className="text-3xl font-bold text-coffee-secondary">85%</div>
                <div className="mt-2 text-sm text-gray-600">Packaging made from recycled materials</div>
              </div>
            </div>
            <div className="p-6 bg-orange-50 rounded-xl border border-gray-200 flex items-start gap-4">
              <img src="https://api.iconify.design/lucide-globe.svg?color=%235C3A21" alt="Globe icon" className="w-6 h-6"/>
              <div>
                <div className="text-3xl font-bold text-coffee-secondary">30%</div>
                <div className="mt-2 text-sm text-gray-600">Carbon footprint reduction since 2022</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="w-full bg-white py-16 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="relative rounded-xl overflow-hidden shadow-xl">
            <img src="https://app.grapesjs.com/api/assets/random-image?query=%22uganda%20coffee%20farmers%22&w=1200&h=900" alt="Farmers sorting coffee cherries" className="w-full h-96 object-cover"/>
          </div>
          <div className="flex flex-col gap-5">
            <h2 className="font-serif text-4xl text-coffee-dark">Ethical Sourcing & Community</h2>
            <p className="mt-2 text-sm text-gray-600">
              We source directly from smallholder farmers across Uganda, paying transparent premiums that reward quality and sustainable practices. Our relationships are built on trust, knowledge sharing, and long-term growth—so when you choose Funzo Coffee, you invest in communities.
            </p>
            <ul className="flex flex-col gap-4">
              <li className="flex items-start gap-3">
                <img src="https://api.iconify.design/lucide-handshake.svg?color=%235C3A21" alt="Handshake icon" className="w-5 h-5"/>
                <div>
                  <div className="font-semibold text-coffee-dark">Transparent trade</div>
                  <div className="mt-2 text-sm text-gray-600">Direct contracts, published premiums, and timely payments.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <img src="https://api.iconify.design/lucide-graduation-cap.svg?color=%235C3A21" alt="Cap icon" className="w-5 h-5"/>
                <div>
                  <div className="font-semibold text-coffee-dark">Mafunzo trainings</div>
                  <div className="mt-2 text-sm text-gray-600">Seasonal workshops on agronomy, post-harvest handling, and quality.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <img src="https://api.iconify.design/lucide-users.svg?color=%235C3A21" alt="Users icon" className="w-5 h-5"/>
                <div>
                  <div className="font-semibold text-coffee-dark">Community development</div>
                  <div className="text-sm text-gray-600">School support, clean water access, and women-led co-ops.</div>
                </div>
              </li>
            </ul>
            <div className="flex flex-col sm:flex-row items-center gap-4 mt-6">
              <a href="/about" className="px-6 py-3 border border-gray-300 rounded-lg text-coffee-primary transition-colors duration-200 hover:text-coffee-secondary hover:border-coffee-secondary focus:outline-none focus:ring-2 focus:ring-coffee-accent">Learn about our relationships</a>
              <a href="/coffee" className="px-6 py-3 bg-coffee-primary text-white rounded-lg shadow-md transition-all duration-150 hover:bg-coffee-dark focus:outline-none focus:ring-2 focus:ring-coffee-accent">Shop ethically sourced</a>
            </div>
          </div>
        </div>
      </section>
      <section className="w-full bg-orange-50 py-16 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8">
            <h2 className="font-serif text-4xl text-coffee-dark">Sustainable Farming Practices</h2>
            <p className="mt-2 text-sm text-gray-600">We promote practices that protect soils, conserve water, and enhance biodiversity.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <img loading="lazy" src="/images/mulching.jpg" alt="Mulching practice in coffee farm" className="w-full h-56 object-cover"/>
              <div className="p-5">
                <div className="font-semibold text-coffee-primary mb-1">Mulching & soil health</div>
                <div className="mt-2 text-sm text-gray-600">Organic mulches enrich soil, retain moisture, and reduce erosion.</div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <img loading="lazy" src="/images/Shade-grown.jpeg" alt="Shade-grown coffee trees" className="w-full h-56 object-cover"/>
              <div className="p-5">
                <div className="font-semibold text-coffee-primary mb-1">Shade-grown biodiversity</div>
                <div className="mt-2 text-sm text-gray-600">Native shade trees support birds, pollinators, and stable microclimates.</div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <img loading="lazy" src="/images/Water-smart irrigation.jpg" alt="Water conservation on a farm" className="w-full h-56 object-cover"/>
              <div className="p-5">
                <div className="font-semibold text-coffee-primary mb-1">Water-smart irrigation</div>
                <div className="mt-2 text-sm text-gray-600">Drip systems and rainwater harvesting reduce water use without sacrificing yield.</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="w-full bg-white py-16 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="flex flex-col gap-5">
            <h2 className="font-serif text-4xl text-coffee-dark">Support for Local Farmers</h2>
            <p className="mt-2 text-sm text-gray-600">We provide targeted support across the season—ensuring farmers have the tools, knowledge, and resources to thrive.</p>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <img src="https://api.iconify.design/lucide-sprout.svg?color=%235C3A21" alt="Sprout icon" className="w-5 h-5"/>
                <div>
                  <div className="font-semibold text-coffee-primary">Pre-season: Training & seedlings</div>
                  <div className="mt-2 text-sm text-gray-600">Distribution of climate-resilient seedlings and agronomy workshops.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <img src="https://api.iconify.design/lucide-tractor.svg?color=%235C3A21" alt="Harvest icon" className="w-5 h-5"/>
                <div>
                  <div className="font-semibold text-coffee-primary">Harvest: Quality premiums</div>
                  <div className="mt-2 text-sm text-gray-600">Fair, transparent premiums for ripe cherry and careful processing.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <img src="https://api.iconify.design/lucide-factory.svg?color=%235C3A21" alt="Factory icon" className="w-5 h-5"/>
                <div>
                  <div className="font-semibold text-coffee-primary">Post-harvest: Value addition</div>
                  <div className="mt-2 text-sm text-gray-600">Training in drying, milling, and quality control for better market outcomes.</div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-6">
              <div className="mt-2 text-sm text-gray-600">Farm income increase (avg. +18%)</div>
              <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full w-[60%] bg-gradient-to-r from-coffee-secondary to-transparent"></div>
              </div>
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden shadow-xl">
            <img loading="lazy" src="https://app.grapesjs.com/api/assets/random-image?query=%22uganda%20coffee%20training%22&w=1200&h=900" alt="Training session with local farmers" className="w-full h-96 object-cover"/>
          </div>
        </div>
      </section>
      <section className="w-full bg-orange-50 py-16 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="relative rounded-xl overflow-hidden shadow-xl">
            <img loading="lazy" src="/images/Funzoparckage.jpg?query=%22eco%20friendly%20coffee%20packaging%22&w=1200&h=900" alt="Eco-friendly coffee packaging" className="w-full h-96 object-cover"/>
          </div>
          <div className="flex flex-col gap-5">
            <h2 className="font-serif text-4xl text-coffee-dark">Eco-friendly Packaging</h2>
            <p className="mt-2 text-sm text-gray-600">Our packaging is designed to reduce waste and maintain flavor integrity.</p>
            <ul className="flex flex-col gap-4">
              <li className="flex items-start gap-3">
                <img src="https://api.iconify.design/lucide-package.svg?color=%235C3A21" alt="Package icon" className="w-5 h-5"/>
                <div>
                  <div className="font-semibold text-coffee-dark">Recycled materials</div>
                  <div className="mt-2 text-sm text-gray-600">Bags with 85% recycled content where supply chains allow.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <img src="https://api.iconify.design/lucide-shield-check.svg?color=%235C3A21" alt="Shield icon" className="w-5 h-5"/>
                <div>
                  <div className="font-semibold text-coffee-dark">Compostable options</div>
                  <div className="mt-2 text-sm text-gray-600">Pilot program for home-compostable liners in local markets.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <img src="https://api.iconify.design/lucide-scissors.svg?color=%235C3A21" alt="Scissors icon" className="w-5 h-5"/>
                <div>
                  <div className="font-semibold text-coffee-dark">Design for disassembly</div>
                  <div className="mt-2 text-sm text-gray-600">Simple labels and reseals for easier recycling.</div>
                </div>
              </li>
            </ul>
            <div className="mt-6">
              <a href="/coffee" className="px-6 py-3 bg-coffee-primary text-white rounded-lg shadow-md transition-all duration-150 hover:bg-coffee-dark focus:outline-none focus:ring-2 focus:ring-coffee-accent">Shop eco-packaged coffee</a>
            </div>
          </div>
        </div>
      </section>
      <section className="w-full bg-white py-16 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8">
            <h2 className="font-serif text-4xl text-coffee-dark">Reducing Our Carbon Footprint</h2>
            <p className="mt-2 text-sm text-gray-600">We measure emissions, set targets, and act across the value chain.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-5 bg-orange-50 rounded-xl border border-gray-200 flex items-start gap-3">
              <img src="https://api.iconify.design/lucide-bus.svg?color=%235C3A21" alt="Transport icon" className="w-5 h-5 mt-1"/>
              <div>
                <div className="font-semibold text-coffee-primary">Optimized logistics</div>
                <div className="mt-2 text-sm text-gray-600">Route planning and consolidated shipments cut transport emissions.</div>
              </div>
            </div>
            <div className="p-5 bg-orange-50 rounded-xl border border-gray-200 flex items-start gap-3">
              <img src="https://api.iconify.design/lucide-flame.svg?color=%235C3A21" alt="Energy icon" className="w-5 h-5 mt-1"/>
              <div>
                <div className="font-semibold text-coffee-primary">Efficient processing</div>
                <div className="mt-2 text-sm text-gray-600">Energy-efficient dryers and mill equipment reduce power demand.</div>
              </div>
            </div>
            <div className="p-5 bg-orange-50 rounded-xl border border-gray-200 flex items-start gap-3">
              <img src="https://api.iconify.design/lucide-tree-pine.svg?color=%235C3A21" alt="Tree icon" className="w-5 h-5 mt-1"/>
              <div>
                <div className="font-semibold text-coffee-primary">Reforestation</div>
                <div className="mt-2 text-sm text-gray-600">Tree-planting with communities offsets residual emissions.</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
            <div className="p-5 bg-orange-50 rounded-xl border border-gray-200">
              <div className="mt-2 text-sm text-gray-600">2025 Reduction Target: 35%</div>
              <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full w-[70%] bg-gradient-to-r from-coffee-primary to-transparent"></div>
              </div>
              <div className="mt-2 text-sm text-gray-600">Current progress: 70%</div>
            </div>
            <div className="p-5 bg-orange-50 rounded-xl border border-gray-200">
              <div className="mt-2 text-sm text-gray-600">Renewable energy adoption: 50% facilities</div>
              <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full w-[50%] bg-gradient-to-r from-coffee-primary to-transparent"></div>
              </div>
              <div className="mt-2 text-sm text-gray-600">Solar pilots in processing hubs underway.</div>
            </div>
          </div>
        </div>
      </section>
      <section className="w-full bg-orange-50 py-16 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8">
            <h2 className="font-serif text-4xl text-coffee-dark">Community Projects</h2>
            <p className="mt-2 text-sm text-gray-600">We partner with local leaders to fund high-impact initiatives in coffee-growing regions.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <img loading="lazy" src="/images/scholarships.jpg?query=%22uganda%20school%20community%22&w=800&h=520" alt="School support project" className="w-full h-56 object-cover"/>
              <div className="p-5">
                <div className="font-semibold text-coffee-primary mb-1">School support</div>
                <div className="mt-2 text-sm text-gray-600">Scholarships, supplies, and classroom renovations in rural districts.</div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <img loading="lazy" src="/images/Clean water access.jpg?query=%22uganda%20clean%20water%22&w=800&h=520" alt="Clean water project" className="w-full h-56 object-cover"/>
              <div className="p-5">
                <div className="font-semibold text-coffee-primary mb-1">Clean water access</div>
                <div className="mt-2 text-sm text-gray-600">Boreholes and filtration systems near farming communities.</div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <img loading="lazy" src="/images/B69A1426.jpg?query=%22uganda%20women%20cooperative%22&w=800&h=520" alt="Women cooperative" className="w-full h-56 object-cover"/>
              <div className="p-5">
                <div className="font-semibold text-coffee-primary mb-1">Women-led co-ops</div>
                <div className="mt-2 text-sm text-gray-600">Leadership training and microfinance support for women farmers.</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="w-full bg-white py-16 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col gap-2 mb-8">
            <h2 className="font-serif text-4xl text-coffee-dark">Voices from the Community</h2>
            <p className="mt-2 text-sm text-gray-600">Farmers and partners share how Mafunzo-inspired programs change lives.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-5 bg-orange-50 rounded-2xl shadow-sm border border-gray-200 flex flex-col gap-5 items-start transition-all duration-300 hover:translate-y-[-5px] hover:shadow-md">
              <div className="flex items-center gap-3">
                <img loading="lazy" src="https://app.grapesjs.com/api/assets/random-image?query=%22uganda%20farmer%20portrait%22&w=160&h=160" alt="Farmer portrait" className="w-14 h-14 rounded-full object-cover border border-gray-200"/>
                <div>
                  <div className="font-semibold text-coffee-primary">Mary N.</div>
                  <div className="text-xs text-gray-600">Smallholder farmer, Mt. Elgon</div>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600">With training, my yields improved and I earn premiums for quality. Our cooperative now supports women’s savings groups.</p>
            </div>
            <div className="p-5 bg-orange-50 rounded-2xl shadow-sm border border-gray-200 flex flex-col gap-5 items-start transition-all duration-300 hover:translate-y-[-5px] hover:shadow-md">
              <div className="flex items-center gap-3">
                <img loading="lazy" src="https://app.grapesjs.com/api/assets/random-image?query=%22uganda%20community%20leader%22&w=160&h=160" alt="Community leader portrait" className="w-14 h-14 rounded-full object-cover border border-gray-200"/>
                <div>
                  <div className="font-semibold text-coffee-primary">David K.</div>
                  <div className="text-xs text-gray-600">Community coordinator</div>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600">Funzo’s borehole project changed daily life—children spend more time in school, and families are healthier.</p>
            </div>
            <div className="p-5 bg-orange-50 rounded-2xl shadow-sm border border-gray-200 flex flex-col gap-5 items-start transition-all duration-300 hover:translate-y-[-5px] hover:shadow-md">
              <div className="flex items-center gap-3">
                <img loading="lazy" src="https://app.grapesjs.com/api/assets/random-image?query=%22uganda%20coffee%20processor%22&w=160&h=160" alt="Processor portrait" className="w-14 h-14 rounded-full object-cover border border-gray-200"/>
                <div>
                  <div className="font-semibold text-coffee-primary">Aisha P.</div>
                  <div className="text-xs text-gray-600">Processing manager</div>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600">Energy-efficient upgrades cut our electricity bills and carbon footprint—without compromising cup quality.</p>
            </div>
          </div>
        </div>
      </section>
      <section className="w-full bg-white py-16 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="flex flex-col gap-5">
            <h2 className="font-serif text-4xl text-coffee-dark">Make the Sustainability Pledge</h2>
            <p className="mt-2 text-sm text-gray-600">Join us in championing sustainable coffee from farm to cup.</p>
            <form method="post" action="#" className="flex flex-col gap-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <label htmlFor="name" className="mt-2 text-sm text-gray-600">Full name</label>
                  <input type="text" id="name" name="name" placeholder="Your name" required className="p-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-secondary"/>
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="mt-2 text-sm text-gray-600">Email</label>
                  <input type="email" id="email-2" name="email" placeholder="you@example.com" required className="p-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-secondary"/>
                </div>
              </div>
              <fieldset className="flex flex-col gap-3 mt-2">
                <legend className="mt-2 text-sm text-gray-600">Your pledge</legend>
                <div className="flex flex-col gap-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" name="pledge_support" aria-label="I support sustainable coffee" className="sr-only peer"/>
                    <span className="w-5 h-5 border border-gray-300 rounded-md flex items-center justify-center peer-checked:bg-coffee-secondary peer-checked:border-coffee-secondary transition-colors duration-200">
                      <img src="https://api.iconify.design/lucide-check.svg?color=%23FFFFFF" alt="Checked" className="w-4 h-4 opacity-0 peer-checked:opacity-100"/>
                    </span>
                    <span className="mt-2 text-sm text-gray-600">I support sustainable coffee production.</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" name="pledge_recycle" aria-label="I will recycle packaging" className="sr-only peer"/>
                    <span className="w-5 h-5 border border-gray-300 rounded-md flex items-center justify-center peer-checked:bg-coffee-secondary peer-checked:border-coffee-secondary transition-colors duration-200">
                      <img src="https://api.iconify.design/lucide-check.svg?color=%23FFFFFF" alt="Checked" className="w-4 h-4 opacity-0 peer-checked:opacity-100"/>
                    </span>
                    <span className="mt-2 text-sm text-gray-600">I will recycle or compost packaging where possible.</span>
                  </label>
                </div>
              </fieldset>
              <button type="submit" className="px-6 py-3 bg-coffee-primary text-white rounded-lg shadow-md transition-all duration-150 hover:bg-coffee-dark focus:outline-none focus:ring-2 focus:ring-coffee-accent mt-4">Sign the pledge</button>
            </form>
          </div>
          <div className="relative rounded-xl overflow-hidden shadow-xl">
            <img loading="lazy" src="https://app.grapesjs.com/api/assets/random-image?query=%22uganda%20community%20coffee%22&w=1200&h=900" alt="Community members with coffee seedlings" className="w-full h-96 object-cover"/>
          </div>
        </div>
      </section>
      <section className="w-full bg-orange-50 py-12 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl text-center sm:text-left">
            <h3 className="text-lg font-semibold text-coffee-dark leading-relaxed mb-2">Choose coffee that empowers communities and protects nature.</h3>
            <p className="mt-2 text-sm text-gray-600">Every bag of Funzo Coffee supports ethical sourcing, sustainable farming, and community development across Uganda.</p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <a href="/coffee" className="px-6 py-3 bg-coffee-primary text-white rounded-lg shadow-md transition-all duration-150 hover:bg-coffee-dark focus:outline-none focus:ring-2 focus:ring-coffee-accent">Shop Sustainable Coffee</a>
            <a href="/about" className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-coffee-primary transition-colors duration-200 hover:text-coffee-secondary hover:border-coffee-secondary focus:outline-none focus:ring-2 focus:ring-coffee-accent">Learn Our Story</a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Sustainability;
